import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  BeakerIcon,
  CubeIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const CustomLabServices = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('testing');

  const serviceCategories = [
    {
      id: 'testing',
      name: t('customLabServices.categories.testing.name'),
      icon: BeakerIcon,
      description: t('customLabServices.categories.testing.description')
    },
    {
      id: 'analysis',
      name: t('customLabServices.categories.analysis.name'),
      icon: MagnifyingGlassIcon,
      description: t('customLabServices.categories.analysis.description')
    },
    {
      id: 'equipment',
      name: t('customLabServices.categories.equipment.name'),
      icon: CogIcon,
      description: t('customLabServices.categories.equipment.description')
    },
    {
      id: 'research',
      name: t('customLabServices.categories.research.name'),
      icon: ClipboardDocumentListIcon,
      description: t('customLabServices.categories.research.description')
    }
  ];

  const services = {
    testing: [
      {
        name: t('customLabServices.testing.medical.name'),
        description: t('customLabServices.testing.medical.description'),
        features: [
          t('customLabServices.testing.medical.feature1'),
          t('customLabServices.testing.medical.feature2'),
          t('customLabServices.testing.medical.feature3'),
          t('customLabServices.testing.medical.feature4')
        ]
      },
      {
        name: t('customLabServices.testing.chemical.name'),
        description: t('customLabServices.testing.chemical.description'),
        features: [
          t('customLabServices.testing.chemical.feature1'),
          t('customLabServices.testing.chemical.feature2'),
          t('customLabServices.testing.chemical.feature3'),
          t('customLabServices.testing.chemical.feature4')
        ]
      },
      {
        name: t('customLabServices.testing.microbiology.name'),
        description: t('customLabServices.testing.microbiology.description'),
        features: [
          t('customLabServices.testing.microbiology.feature1'),
          t('customLabServices.testing.microbiology.feature2'),
          t('customLabServices.testing.microbiology.feature3'),
          t('customLabServices.testing.microbiology.feature4')
        ]
      }
    ],
    analysis: [
      {
        name: t('customLabServices.analysis.data.name'),
        description: t('customLabServices.analysis.data.description'),
        features: [
          t('customLabServices.analysis.data.feature1'),
          t('customLabServices.analysis.data.feature2'),
          t('customLabServices.analysis.data.feature3'),
          t('customLabServices.analysis.data.feature4')
        ]
      },
      {
        name: t('customLabServices.analysis.quality.name'),
        description: t('customLabServices.analysis.quality.description'),
        features: [
          t('customLabServices.analysis.quality.feature1'),
          t('customLabServices.analysis.quality.feature2'),
          t('customLabServices.analysis.quality.feature3'),
          t('customLabServices.analysis.quality.feature4')
        ]
      }
    ],
    equipment: [
      {
        name: t('customLabServices.equipment.calibration.name'),
        description: t('customLabServices.equipment.calibration.description'),
        features: [
          t('customLabServices.equipment.calibration.feature1'),
          t('customLabServices.equipment.calibration.feature2'),
          t('customLabServices.equipment.calibration.feature3'),
          t('customLabServices.equipment.calibration.feature4')
        ]
      },
      {
        name: t('customLabServices.equipment.maintenance.name'),
        description: t('customLabServices.equipment.maintenance.description'),
        features: [
          t('customLabServices.equipment.maintenance.feature1'),
          t('customLabServices.equipment.maintenance.feature2'),
          t('customLabServices.equipment.maintenance.feature3'),
          t('customLabServices.equipment.maintenance.feature4')
        ]
      }
    ],
    research: [
      {
        name: t('customLabServices.research.development.name'),
        description: t('customLabServices.research.development.description'),
        features: [
          t('customLabServices.research.development.feature1'),
          t('customLabServices.research.development.feature2'),
          t('customLabServices.research.development.feature3'),
          t('customLabServices.research.development.feature4')
        ]
      },
      {
        name: t('customLabServices.research.validation.name'),
        description: t('customLabServices.research.validation.description'),
        features: [
          t('customLabServices.research.validation.feature1'),
          t('customLabServices.research.validation.feature2'),
          t('customLabServices.research.validation.feature3'),
          t('customLabServices.research.validation.feature4')
        ]
      }
    ]
  };

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: t('customLabServices.benefits.quality.title'),
      description: t('customLabServices.benefits.quality.description')
    },
    {
      icon: ClockIcon,
      title: t('customLabServices.benefits.speed.title'),
      description: t('customLabServices.benefits.speed.description')
    },
    {
      icon: UserGroupIcon,
      title: t('customLabServices.benefits.expertise.title'),
      description: t('customLabServices.benefits.expertise.description')
    },
    {
      icon: CubeIcon,
      title: t('customLabServices.benefits.equipment.title'),
      description: t('customLabServices.benefits.equipment.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-green via-brand-blue to-brand-blueSecondary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <BeakerIcon className="w-16 h-16" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('customLabServices.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {t('customLabServices.hero.subtitle')}
            </p>
            <div className="bg-yellow-500 text-yellow-900 px-6 py-3 rounded-full inline-block font-semibold text-lg">
              {t('customLabServices.hero.comingSoon')}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Categories */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('customLabServices.categories.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('customLabServices.categories.subtitle')}
            </p>
          </motion.div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === category.id
                    ? 'bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <category.icon className="w-6 h-6" />
                {category.name}
              </button>
            ))}
          </div>

          {/* Active Category Services */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services[activeTab]?.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.name}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 ml-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('customLabServices.benefits.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('customLabServices.benefits.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="p-4 bg-gradient-to-r from-brand-green to-brand-blue rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('customLabServices.howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('customLabServices.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-8 items-center">
            {[1, 2, 3, 4, 5].map((step, index) => (
              <React.Fragment key={step}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-green to-brand-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                    {step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {t(`customLabServices.howItWorks.step${step}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t(`customLabServices.howItWorks.step${step}.description`)}
                  </p>
                </motion.div>
                {index < 4 && (
                  <div className="hidden md:flex justify-center">
                    <ArrowRightIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('customLabServices.industries.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('customLabServices.industries.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((industry, index) => (
              <motion.div
                key={industry}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl text-center hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-bold text-gray-900">
                  {t(`customLabServices.industries.industry${industry}`)}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-green to-brand-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <SparklesIcon className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">
              {t('customLabServices.cta.title')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('customLabServices.cta.subtitle')}
            </p>
            <div className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-full inline-block font-bold text-xl">
              {t('customLabServices.cta.comingSoon')}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomLabServices;
